import React from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DarkModeProvider, useDarkMode } from './contexts/DarkModeContext';
import ProtectedRoute from './components/ProtectedRoute';
import AddDevice from './components/AddDevice';
import DeviceCardList from './components/DeviceCardList';
import TopChannels from './components/TopChannels';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Admin from './pages/Admin';
import Help from './pages/Help';
import DeviceDetail from './pages/DeviceDetail';
import deviceService, { Device } from './services/deviceService';
import { setupPWAInstallPrompt, checkForServiceWorkerUpdates } from './utils/pwaUtils';
import './App.css';
import './styles/Devices.css';
import './styles/Admin.css';
import { useState, useEffect } from 'react';

const Home: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [devicesError, setDevicesError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    setLoadingDevices(true);
    setDevicesError('');
    try {
      const userDevices = await deviceService.getDevices();
      setDevices(userDevices);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to load devices';
      setDevicesError(message);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleDeviceAdded = (newDevice: Device) => {
    setDevices([newDevice, ...devices]);
  };

  const handleDeviceDeleted = (deviceId: number) => {
    setDevices(devices.filter(d => d.id !== deviceId));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            Stream Signal
          </Link>
          <button 
            className="hamburger-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {user ? (
              <>
                <span className="nav-user">Welcome, {user.username}</span>
                <Link to="/help" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Help
                </Link>
                <button onClick={toggleDarkMode} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {isDarkMode ? '☀️' : '🌙'}
                </button>
                <button onClick={handleLogout} className="nav-logout">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/help" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Help
                </Link>
                <button onClick={toggleDarkMode} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {isDarkMode ? '☀️' : '🌙'}
                </button>
                <Link to="/login" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Login
                </Link>
                <Link to="/register" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {user && (
        <main className="main-content">
          <div className="devices-container">
            <AddDevice onDeviceAdded={handleDeviceAdded} />
            
            {loadingDevices ? (
              <div className="loading">Loading devices...</div>
            ) : devicesError ? (
              <div className="error-message">{devicesError}</div>
            ) : (
              <DeviceCardList devices={devices} />
            )}

            {/* Top Channels Section */}
            <TopChannels />
          </div>
        </main>
      )}
    </div>
  );
};

const AppContent: React.FC = () => {
  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/device/:deviceId"
          element={
            <ProtectedRoute>
              <DeviceDetail />
            </ProtectedRoute>
          }
        />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/help" element={<Help />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Routes>
      <footer className="app-footer">
        <p>
          Powered by{' '}
          <a href="https://yt-link.com" target="_blank" rel="noopener noreferrer">
            yt-link.com
          </a>
        </p>
      </footer>
    </>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Setup PWA features
    setupPWAInstallPrompt();
    checkForServiceWorkerUpdates();
  }, []);

  return (
    <BrowserRouter>
      <DarkModeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DarkModeProvider>
    </BrowserRouter>
  );
};

export default App;
